"use client";

import { animate, useReducedMotion } from "framer-motion";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

const EASE = [0.22, 1, 0.36, 1] as const;

const TEXT_INVIEW_SELECTOR = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "li",
  "blockquote",
  "figcaption",
  "dd",
  "dt",
  "td",
  "th",
  "pre",
  "address",
  "cite",
  "summary",
  "label",
  "legend",
  "a",
  "button",
].join(", ");

export function SiteBodyWithInviewTextMotion({
  children,
}: Readonly<{ children: ReactNode }>) {
  const pathname = usePathname();
  const containerRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion) return;

    const root = containerRef.current;
    if (!root) return;

    const prepared = new WeakSet<Element>();
    const revealed = new WeakSet<HTMLElement>();
    const pending = new Set<HTMLElement>();

    const reveal = (el: HTMLElement) => {
      if (revealed.has(el)) return;
      revealed.add(el);
      pending.delete(el);
      try {
        io.unobserve(el);
      } catch {}
      animate(el, { opacity: 1 }, { duration: 0.63, ease: EASE });
    };

    const overlapsInflatedViewport = (el: HTMLElement, padPx: number) => {
      if (!el.isConnected) return false;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const top = -padPx;
      const left = -padPx;
      const bottom = vh + padPx;
      const right = vw + padPx;
      return r.bottom > top && r.top < bottom && r.right > left && r.left < right;
    };

    const flushPendingByGeometry = () => {
      const pad = Math.max(96, Math.round(window.innerHeight * 0.15));
      for (const el of [...pending]) {
        if (!el.isConnected) {
          pending.delete(el);
          continue;
        }
        if (overlapsInflatedViewport(el, pad)) reveal(el);
      }
    };

    let flushRaf = 0;
    const scheduleFlush = () => {
      if (flushRaf) cancelAnimationFrame(flushRaf);
      flushRaf = requestAnimationFrame(() => {
        flushRaf = 0;
        flushPendingByGeometry();
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const t = entry.target;
          if (t instanceof HTMLElement) reveal(t);
        }
      },
      {
        root: null,
        rootMargin: "20% 0px 35% 0px",
        threshold: [0, 0.001, 0.01, 0.05],
      },
    );

    const bind = (el: HTMLElement) => {
      if (prepared.has(el)) return;
      prepared.add(el);
      pending.add(el);
      io.observe(el);
      scheduleFlush();
    };

    const scan = () => {
      root.querySelectorAll(TEXT_INVIEW_SELECTOR).forEach((node) => {
        if (node instanceof HTMLElement) bind(node);
      });
    };

    scan();

    const mo = new MutationObserver(() => {
      scan();
    });
    mo.observe(root, { childList: true, subtree: true });

    const onScrollOrResize = () => scheduleFlush();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    requestAnimationFrame(() => {
      scheduleFlush();
      requestAnimationFrame(scheduleFlush);
    });

    return () => {
      mo.disconnect();
      io.disconnect();
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (flushRaf) cancelAnimationFrame(flushRaf);
    };
  }, [pathname, reduceMotion]);

  return (
    <div
      ref={containerRef}
      className="flex min-h-0 flex-1 flex-col"
      data-inview-text-scope=""
    >
      {children}
    </div>
  );
}
